<template>
  <div>
    <b-modal v-model="showReleaseFormManager" size="lg" @ok="handleReleaseFormDidSelectItems"
             :ok-disabled="!releaseFormModalOk" @hidden="releaseFormModalOk = false">
      <multi-release-form-manager
          ref="releaseFormManager"
          @releaseFormSelected="releaseFormModalOk = true"
          @releaseFormCleared="releaseFormModalOk = false"
          :assets-release-forms="assetsReleaseForms"
          :selected-asset-ids="selectedAssetIds"
          :initial-release-forms="submissionReleaseForms"
          :release-form-url="releaseFormUrl"/>
    </b-modal>

    <bulk-selection-bar :items="files"
                        v-model="assetSelection"
                        :options-always-visible="true"
                        :bulk-action-running="bulkActionRunning">
      <template v-slot:menuSlot>
        <b-button variant="outline"
                  class="btn-rounded"
                  v-b-tooltip
                  :title="selectedAssetsCount === 0 ? 'Select some assets first' : ''"
                  :disabled="bulkActionRunning"
                  @click="showReleaseFormManager = true">
          Add Release Forms
        </b-button>

        <b-button variant="outline"
                  class="btn-rounded"
                  v-b-tooltip
                  :title="selectedAssetsCount === 0 ? 'Select some assets first' : ''"
                  :disabled="bulkActionRunning"
                  @click="handleNoReleaseNeeded()">
          No Release Needed
        </b-button>
      </template>
    </bulk-selection-bar>

    <b-card class="my-3">
      <p v-if="files.length > 0">{{ addAssetsHint }}</p>

      <div class="mb-1 asset-avatar-select-overlay" v-for="file in files" :key="`${file.id}-${file.updated_at}`">
        <div class="bg-light p-3 d-flex align-items-center" :class="submissionAssetClass(file)">

          <SubmissionAsset :asset="file"
                           :url="url"
                           :release-forms="assetsReleaseForms[file.id]"
                           :release-form-url="releaseFormUrl"
                           :categories="categories"
                           :tag-store="tagStore"
                           @addRelease="handleAddSingleReleaseForm(file)"
                           @remove="handleAssetRemove">
            <template v-slot:assetPreviewSlot>
              <div @click.prevent.stop="handleAssetSelection(file)"
                   class="image-select-control"
                   :class="{ 'selection-area-expanded': selectedAssetsCount > 0 }">
                <b-form-checkbox v-model="assetSelection[file.id]" @click.stop/>
              </div>
            </template>
          </SubmissionAsset>
        </div>
      </div>

      <div v-if="files.length === 0">
        <div class="card-body text-center">
          <div class="py-5">
            <h6>No uploaded assets yet</h6>
            <div class="text-center">Start uploading assets by dragging them on this window</div>
            <div>
              or <span class="text-primary cursor-pointer" @click="handleAddAssets">upload files</span>
            </div>
            <p class="mt-2">{{ addAssetsHint }}</p>
          </div>
        </div>
      </div>

      <MountingPortal mountTo="#submission-images-form-add-assets">
        <b-button variant="primary"
                  class="btn-rounded mr-2 mb-2"
                  v-if="canAddAssets"
                  @click="handleAddAssets()">
          {{ addAssetsButtonTitle }}
        </b-button>
      </MountingPortal>

      <SubmissionUploadManager
          ref="uploadManager"
          :asset-type="assetType"
          :submission="submission"
          :tags-by-category="tagStore.tagsByCategory"
          :categories="categories"
          :url="url"
          :param-name="paramName"
          :release-forms="submissionReleaseForms"
          :release-forms-needed="releaseFormsNeeded"
          :release-form-url="releaseFormUrl"
          :files-count="files.length"
          :max-files="maxFiles"
          :max-file-size="maxFileSize"
          drop-area=".my-assets-dropzone"
          @canAddAssets="canAddAssets = arguments[0]"
          @removeFileId="handleAssetIdRemove"
          @addedAsset="handleAddedAsset"/>
    </b-card>
  </div>
</template>

<script>
import SubmissionAsset from './SubmissionAsset'
import SubmissionUploadManager from './SubmissionUploadManager'
import generalMixins from '../../../mixins/GeneralMixins'
import MultiReleaseFormManager from '../../ReleaseForms/MultiReleaseFormManager'
import BulkSelectionBar from "../../Common/BulkSelectionBar";
import normalize from "json-api-normalize";
import Attributes from "../../../models/Attributes";
import consumer from "../../../../channels/consumer";
import PulseLoader from 'vue-spinner/src/PulseLoader';

export default {
  props: [
    'url',
    'releaseFormUrl',
    'paramName',
    'dropArea',
    'categories',
    'tagStore',
    'submission',
    'assets',
    'invalidAssetIds',
    'addAssetsHint',
    'submissionReleaseForms',
    'maxFiles',
    'maxFileSize',
    'assetType'
  ],

  data: function () {
    return {
      files: this.assets || [],
      showReleaseFormManager: false,
      assetSelection: {},
      assetsReleaseForms: this.getInitialAssetsReleaseForms(),
      canAddAssets: true,
      bulkActionRunning: false,
      releaseFormModalOk: false
    }
  },

  components: {SubmissionAsset, SubmissionUploadManager, MultiReleaseFormManager, BulkSelectionBar, PulseLoader},

  mixins: [generalMixins],

  mounted() {
    this.subscribeToSocket()
  },

  computed: {
    addAssetsButtonTitle() {
      if (this.assetType === "photo") {
        return "Add Photos"
      } else if (this.assetType === "video") {
        return "Add Videos"
      } else if (this.assetType === "illustration") {
        return "Add Illustrations"
      }
    },
    didSelectAssets: function () {
      return this.selectedAssetIds.length > 0;
    },

    selectedAssetIds: function () {
      return Object.keys(this.assetSelection).filter(key => this.assetSelection[key] === true);
    },

    selectedAssetsCount: function () {
      return this.selectedAssetIds.length;
    },

    selectionMode: function () {
      return this.selectedAssetsCount > 0;
    },

    releaseFormsNeeded: function () {
      return this.submission.asset_usage_type === 'commercial';
    }
  },

  methods: {
    /// Returns an array of asset ids that need to be set on a release form based on the current asset selection
    /// Will include non-selected assets as well, since applying an asset id list to a release form replaces the entire asset id list
    // assetIdsParams(releaseFormId) {
    //   this.files.filter(file => file.release_forms.map(rf => rf.id).includes(releaseFormId) || this.selectedAssetIds.includes(file.id))
    // },

    subscribeToSocket() {
      const vm = this

      consumer.subscriptions.create("ContributorChannel", {
        connected: () => {
          console.log("Connected to contributor channel");
        },

        disconnected() {
          console.log("Disconn from contributor channel");
        },

        received(data) {
          const json = JSON.parse(data)
          const asset = normalize(json).get(Attributes.assetAttributes.concat(Attributes.prefixed('release_forms', Attributes.releaseFormAttributes)));
          vm.handleUpsertAsset(asset)
        }
      })
    },

    submissionAssetClass: function (file) {
      if (this.isInvalidAsset(file)) {
        return 'border border-danger'
      }

      if (this.assetSelection[file.id]) {
        return 'border border-primary'
      }

      return 'border border-light'
    },

    getInitialAssetsReleaseForms: function () {
      const releaseForms = {};
      this.assets.forEach(f => {
        releaseForms[f.id] = f.release_forms || []
      });
      return releaseForms;
    },

    selectAllAssets: function () {
      const selection = {};
      this.files.forEach(file => {
        selection[file.id] = true
      });
      this.assetSelection = selection;
    },

    unselectAllAssets: function () {
      this.assetSelection = {}
    },

    cancelSelection: function () {
      this.selectedReleaseForm = null;
      this.assetSelection = {}
    },

    isInvalidAsset: function (asset) {
      if (!this.invalidAssetIds) return false;
      return this.invalidAssetIds.includes(asset.id)
    },

    handleAddAssets: function () {
      this.$refs.uploadManager.openFilePicker();
    },

    handleNoReleaseNeeded() {
      this.selectedAssetIds.forEach(id => {
        this.$http.patch(`${this.url}/${id}`, {
          asset: {
            release_required: false
          }
        }).then(fulfilled => {

          const file = this.files.find(f => f.id === id)

          if (file) {
            file.release_required = false
            file.updated_at = Date()
            this.handleUpsertAsset(file)
          }
        });
      })

    },

    handleAddSingleReleaseForm: function (file) {
      this.$set(this.assetSelection, file.id, true);
    },

    handleAddedAsset: function (asset) {
      this.files.push(asset);
      this.assetsReleaseForms[asset.id] = asset.release_forms;
      $('#submit-for-review-button').removeClass('d-none');
    },

    handleUpsertAsset: function (asset) {
      const file = this.files.find(file => file.id === asset.id)
      if (file) {
        this.$set(this.files, this.files.indexOf(file), asset);
      } else {
        this.files.push(asset);
      }

      this.assetsReleaseForms[asset.id] = asset.release_forms;
      $('#submit-for-review-button').removeClass('d-none');
    },

    handleReleaseFormDidSelectItems: function () {
      const selectedReleaseFormIds = this.$refs.releaseFormManager.selectedReleaseForms;
      console.log(selectedReleaseFormIds);
      if (!selectedReleaseFormIds.length) return;

      this.$http.post(`${this.releaseFormUrl}/add_many`, {
        asset_ids: this.selectedAssetIds,
        release_form_ids: selectedReleaseFormIds
      }).then(fulfilled => {
        const responseReleaseForms = normalize(fulfilled.body).get(Attributes.releaseFormAttributes);

        // Append the release forms with ids selectedReleaseFormIds to each assetsReleaseForms[assetId] from selectedAssetIds
        this.selectedAssetIds.forEach(assetId => {
          let assetReleaseForms = this.assetsReleaseForms[assetId] || [];

          responseReleaseForms.forEach(rf => {
            if (!assetReleaseForms.map(r => r.id).includes(rf.id)) {
              assetReleaseForms.push(rf)
            }
          })

          this.assetsReleaseForms[assetId] = assetReleaseForms;
        });

      }, rejected => {
        this.$emit('error', rejected);
      })
    },

    handleAssetSelection: function (file) {
      this.$set(this.assetSelection, file.id, !this.assetSelection[file.id]);
    },

    handleAssetRemove: function (asset) {
      this.$http.delete(`${this.url}/${asset.id}`).then(response => {
        this.files.splice(this.files.indexOf(asset), 1)
      }, error => {
        this.showErrorToast("There was an error removing the file. Please try again later")
      })
    },

    handleAssetIdRemove(id) {
      this.$http.delete(`${this.url}/${id}`)
    }
  }
}
</script>